import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay, tap } from 'rxjs/operators';
import { IRegisteredApplicationsService } from '../../interfaces/services/registered-applications-service.interface';
import {
  CreateRegisteredApplicationRequest,
  RegisteredApplication,
  RegisteredApplicationTopics,
  RegisteredApplicationsListRole,
} from '../../interfaces/state/entities/registered-application.interface';

@Injectable({ providedIn: 'root' })
export class RegisteredApplicationsMockService implements IRegisteredApplicationsService {
  private applications: RegisteredApplication[] = [
    {
      id: '11111111-1111-1111-1111-111111111111',
      clientId: '11111111-1111-1111-1111-111111111111',
      name: 'SiteCapture-Admin',
      roles: ['Admin'],
    },
    {
      id: '22222222-2222-2222-2222-222222222222',
      clientId: '22222222-2222-2222-2222-222222222222',
      name: 'SiteCapture-Client',
      roles: ['Client'],
      topics: {
        drawingProcessed: 'drawings.processed',
        captureProcessed: 'captures.processed',
      },
    },
    {
      id: '33333333-3333-3333-3333-333333333333',
      clientId: '33333333-3333-3333-3333-333333333333',
      name: 'SiteCapture-Processing',
      roles: ['Processing'],
    },
  ];

  getRegisteredApplications(role: RegisteredApplicationsListRole = 'All'): Observable<RegisteredApplication[]> {
    const list =
      role === 'All'
        ? this.applications
        : this.applications.filter((app) => app.roles?.includes(role));
    return of([...list]).pipe(delay(80));
  }

  createRegisteredApplication(request: CreateRegisteredApplicationRequest): Observable<void> {
    const id = request.clientId.trim();
    const topics = this.normalizeTopics(request.topics);
    return of(void 0).pipe(
      delay(60),
      tap(() => {
        this.applications = [
          ...this.applications,
          {
            id,
            clientId: id,
            name: request.name.trim(),
            roles: [request.role],
            ...(topics ? { topics } : {}),
          },
        ];
      })
    );
  }

  private normalizeTopics(
    topics?: RegisteredApplicationTopics
  ): RegisteredApplicationTopics | undefined {
    if (!topics) {
      return undefined;
    }
    const drawingProcessed = topics.drawingProcessed?.trim();
    const captureProcessed = topics.captureProcessed?.trim();
    if (!drawingProcessed && !captureProcessed) {
      return undefined;
    }
    return {
      ...(drawingProcessed ? { drawingProcessed } : {}),
      ...(captureProcessed ? { captureProcessed } : {}),
    };
  }

  deleteRegisteredApplication(id: string): Observable<void> {
    return of(void 0).pipe(
      delay(60),
      tap(() => {
        this.applications = this.applications.filter((a) => a.id !== id);
      })
    );
  }
}
